
/**
 * 原始属性カードのトリガー実装
 *
 * このファイルには原始属性カードの個別トリガー効果を実装します。
 * 各カードは独自のトリガー定義を持ち、triggerEngineによって管理されます。
 */

import { TRIGGER_TYPES, ACTIVATION_TYPES, TRIGGER_PRIORITIES } from '../triggerTypes';
import type { TriggerContext } from '../triggerEngine';
import { registerCardTriggers } from '../triggerEngine';
import type { CardWithId, MonsterInstance } from '../../types/card';
import type { TriggerDefinition } from '../../types/effect';
import {
  getPlayerContext,
  millDeck,
  conditionalDamage,
  searchCard,
  reviveFromGraveyard,
  drawCards,
  healLife,
  destroyMonster,
  modifyAttack,
  modifyHP,
  selectAndApplyStatusToOpponent,
} from '../effectHelpers';
import { hasCategory } from '../../utils/helpers';
import { statusEffectEngine, STATUS_EFFECT_TYPES } from '../statusEffects';
import { continuousEffectEngine } from '../continuousEffects';
import {
  forceSurfaceCard,
  destroyDeepSeaCard,
  calculateSurfaceCost,
} from '../deepSeaZone';

/**
 * 原始属性カードのトリガー定義
 * カードID => トリガー配列のマッピング
 */
export const primitiveCardTriggers = {
  /**
   * C0000001: 粘液獣・開花
   * 【召喚時】このカードは分裂する。場にこのカードが3体以上いる場合分裂不可
   * 【自分エンドフェイズ時】このカードは分裂する。場にこのカードが3体以上いる場合分裂不可
   * 【自壊時】破壊された時一度だけ「粘液獣の種子（攻撃力0HP100）」を場に生成する。
   */
  C0000001: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 分裂する（場に3体未満の場合）',
      effect: (context: TriggerContext) => {
        const { card, addLog, summoningSickness = true } = context;
        const { myField, setMyField, currentPlayer } = getPlayerContext(context);

        // 場の粘液獣・開花の数をカウント（召喚したカード自身を含む）
        const bloomerCount = myField.filter(
          (m) => m && m.id === 'C0000001'
        ).length;

        if (bloomerCount >= 3) {
          addLog('粘液獣・開花: 場に3体以上いるため分裂できない', 'info');
          return;
        }

        // 空きスロットを探す
        const emptySlotIndex = myField.findIndex((slot) => slot === null);
        if (emptySlotIndex === -1) {
          addLog('粘液獣・開花: 場が満杯のため分裂できない', 'info');
          return;
        }

        // 分裂: 同じステータスのコピーを生成
        if (!card) return;
        const monster = card as MonsterInstance;
        const copy = {
          ...monster,
          uniqueId: `${monster.id}_split_${Date.now()}`,
          attack: monster.attack,
          currentAttack: monster.currentAttack ?? monster.attack ?? 0,
          hp: monster.hp,
          currentHp: monster.currentHp ?? monster.hp ?? 0,
          canAttack: !summoningSickness, // 召喚酔い設定に依存
          charges: [],
          statusEffects: [],
          owner: currentPlayer,
        } as MonsterInstance;

        const newField = [...myField];
        newField[emptySlotIndex] = copy;
        setMyField(newField);

        // 分裂したコピーにもトリガーと常時効果を登録
        registerCardTriggers(copy, currentPlayer, emptySlotIndex);
        continuousEffectEngine.register(copy, currentPlayer);

        addLog('粘液獣・開花が分裂した！', 'info');
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンド時: 分裂する（場に3体未満の場合）',
      effect: (context: TriggerContext) => {
        const { card, addLog, summoningSickness = true } = context;
        const { myField, setMyField, currentPlayer } = getPlayerContext(context);

        // 場の粘液獣・開花の数をカウント
        const bloomerCount = myField.filter(
          (m) => m && m.id === 'C0000001'
        ).length;

        if (bloomerCount >= 3) {
          addLog('粘液獣・開花: 場に3体以上いるため分裂できない', 'info');
          return;
        }

        // 空きスロットを探す
        const emptySlotIndex = myField.findIndex((slot) => slot === null);
        if (emptySlotIndex === -1) {
          addLog('粘液獣・開花: 場が満杯のため分裂できない', 'info');
          return;
        }

        // 分裂: 同じステータスのコピーを生成
        if (!card) return;
        const monster = card as MonsterInstance;
        const copy = {
          ...monster,
          uniqueId: `${monster.id}_split_${Date.now()}`,
          attack: monster.attack,
          currentAttack: monster.currentAttack ?? monster.attack ?? 0,
          hp: monster.hp,
          currentHp: monster.currentHp ?? monster.hp ?? 0,
          canAttack: !summoningSickness, // 召喚酔い設定に依存
          charges: [],
          statusEffects: [],
          owner: currentPlayer,
        } as MonsterInstance;

        const newField = [...myField];
        newField[emptySlotIndex] = copy;
        setMyField(newField);

        // 分裂したコピーにもトリガーと常時効果を登録
        registerCardTriggers(copy, currentPlayer, emptySlotIndex);
        continuousEffectEngine.register(copy, currentPlayer);

        addLog('粘液獣・開花が分裂した！', 'info');
      },
    },
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '破壊時: 粘液獣の種子（攻撃力0HP100）を場に生成',
      effect: (context: TriggerContext) => {
        const { addLog, summoningSickness = true } = context;
        const { myField, setMyField, currentPlayer } = getPlayerContext(context);

        // 空きスロットを探す
        const emptySlotIndex = myField.findIndex((slot) => slot === null);
        if (emptySlotIndex === -1) {
          addLog('粘液獣・開花: 場が満杯のため種子を生成できない', 'info');
          return;
        }

        // 粘液獣の種子を生成
        const seed = {
          id: 'C0000001_SEED',
          uniqueId: `C0000001_SEED_${Date.now()}`,
          name: '粘液獣の種子',
          attribute: '原始',
          cost: 1,
          type: 'monster',
          attack: 0,
          hp: 100,
          currentAttack: 0,
          currentHp: 100,
          category: '【プラント】【スライム】',
          effect: '',
          canAttack: !summoningSickness, // 召喚酔い設定に依存
          owner: currentPlayer, // 常時効果のターゲット判定用
          charges: [],
          statusEffects: [],
        } as MonsterInstance;

        const newField = [...myField];
        newField[emptySlotIndex] = seed;
        setMyField(newField);

        addLog('粘液獣・開花が破壊され、粘液獣の種子を生成した！', 'info');
      },
    },
  ],

  /**
   * C0000004: ゴミあさり粘液獣
   * 【召喚時】墓地の《粘液獣》モンスター1体を攻撃力半減して場に戻す。
   * 【召喚時】このターンはダイレクトアタック可能。
   */
  C0000004: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      priority: TRIGGER_PRIORITIES.HIGH,
      description: '召喚時: 墓地の粘液獣を攻撃力半減で復活',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingGraveyardSelection, setShowGraveyardViewer, summoningSickness = true } = context;
        const { myField, setMyField, myGraveyard, setMyGraveyard, currentPlayer } = getPlayerContext(context);

        // 墓地から《粘液獣》モンスター（スライムカテゴリ）を検索
        const slimeMonsters = myGraveyard.filter(card =>
          card.type === 'monster' && hasCategory(card, '【スライム】')
        );

        if (slimeMonsters.length === 0) {
          addLog('ゴミあさり粘液獣: 墓地に《粘液獣》モンスターがいません', 'info');
          return false;
        }

        // 空きスロットを確認
        const emptySlotIndex = myField.findIndex(slot => slot === null);
        if (emptySlotIndex === -1) {
          addLog('ゴミあさり粘液獣: フィールドに空きがありません', 'info');
          return false;
        }

        // 蘇生処理（選択後に実行）
        const reviveCard = (selectedCard: CardWithId) => {
          // 墓地から除去
          setMyGraveyard(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));

          // 攻撃力半減で蘇生
          const revivedMonster = {
            ...selectedCard,
            uniqueId: selectedCard.uniqueId || `${selectedCard.id}_${Date.now()}`,
            attack: Math.floor((selectedCard.attack || 0) / 2),
            currentAttack: Math.floor((selectedCard.attack || 0) / 2),
            hp: selectedCard.hp || 0,
            currentHp: selectedCard.hp || 0,
            canAttack: !summoningSickness,
            owner: currentPlayer,
            chargeCount: 0,
            statusEffects: [],
          } as MonsterInstance;

          setMyField(prev => {
            const newField = [...prev];
            const slot = newField.findIndex(s => s === null);
            if (slot !== -1) {
              newField[slot] = revivedMonster;
            }
            return newField;
          });

          addLog(`ゴミあさり粘液獣: ${selectedCard.name}を墓地から蘇生！（攻撃力半減）`, 'heal');
        };

        // 1体のみの場合は自動選択
        if (slimeMonsters.length === 1) {
          reviveCard(slimeMonsters[0]);
          return true;
        }

        // 複数いる場合は選択UIを表示
        if (setPendingGraveyardSelection && setShowGraveyardViewer) {
          setShowGraveyardViewer(currentPlayer);
          setPendingGraveyardSelection({
            message: '蘇生する《粘液獣》モンスターを選択',
            cards: slimeMonsters,
            count: 1,
            filter: (card: CardWithId) => card.type === 'monster' && hasCategory(card, '【スライム】'),
            callback: (selectedCards: CardWithId[] | CardWithId | null) => {
              // 配列または単一カードとして処理
              const selected = Array.isArray(selectedCards)
                ? (selectedCards.length > 0 ? selectedCards[0] : null)
                : selectedCards;
              if (selected) {
                reviveCard(selected);
              }
            },
            selectionOwner: currentPlayer,
          } as any);
          return true;
        }

        // UIが使えない場合は最初のカードを自動選択
        reviveCard(slimeMonsters[0]);
        return true;
      },
    },
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      priority: TRIGGER_PRIORITIES.NORMAL,
      description: '召喚時: このターンはダイレクトアタック可能',
      effect: (context: TriggerContext) => {
        const { slotIndex, addLog } = context;
        const { setMyField } = getPlayerContext(context);

        // ダイレクトアタック許可フラグを設定
        setMyField((prev) =>
          prev.map((m, idx) => {
            if (idx === slotIndex && m) {
              return { ...m, canDirectAttackThisTurn: true };
            }
            return m;
          })
        );

        addLog(
          'ゴミあさり粘液獣: このターンはダイレクトアタック可能！',
          'info'
        );
      },
    },
  ],

  /**
   * C0000005: 苗床粘液獣
   * 【自分メインフェイズ時】、1ターンに1度、場に粘液獣が3体以上いる場合、相手モンスター1体を捕食して自身の攻撃力をアップ（捕食1体につき300、最大1500まで）。
   * 【自壊時】このカードは1度だけ破壊されると効果無効で墓地から復活。
   */
  C0000005: [
    {
      type: TRIGGER_TYPES.ON_MAIN_PHASE_SELF,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description:
        'メイン時: 場に粘液獣3体以上で相手モンスター捕食（攻撃力+300）',
      effect: (context: TriggerContext) => {
        const { card, slotIndex, addLog, setPendingMonsterTarget } = context;
        const { myField, setMyField, opponentField } = getPlayerContext(context);

        // 場の粘液獣の数をカウント
        const slimeCount = myField.filter(
          (m) => m && hasCategory(m, '【スライム】')
        ).length;

        if (slimeCount < 3) {
          addLog(
            '苗床粘液獣: 場に粘液獣が3体未満のため捕食できない',
            'info'
          );
          return false;
        }

        // 相手モンスターがいるかチェック
        const hasOpponentMonster = opponentField.some((m) => m !== null);
        if (!hasOpponentMonster) {
          addLog('苗床粘液獣: 捕食する対象がいない', 'info');
          return false;
        }

        // 攻撃力上限チェック
        if (slotIndex === undefined) return false;
        const currentMonster = myField[slotIndex] as any;
        if (currentMonster) {
          const currentBonus = currentMonster.predationBonus || 0;
          if (currentBonus >= 1500) {
            addLog('苗床粘液獣: 攻撃力上昇は最大値(+1500)に達している', 'info');
            return false;
          }
        }

        // モンスター選択UIを表示
        if (!setPendingMonsterTarget) return false;
        setPendingMonsterTarget({
          message: '捕食する相手モンスターを選択してください',
          targetPlayer: 'opponent',
          callback: (targetIndex: number) => {
            // 破壊処理
            destroyMonster(context as any, targetIndex, true);

            // 攻撃力アップ（最大1500まで）
            modifyAttack(context as any, 300, slotIndex, false, true);

            // 捕食ボーナスを記録（上限管理用）
            setMyField((prev) =>
              prev.map((m, idx) => {
                if (idx === slotIndex && m) {
                  return {
                    ...m,
                    predationBonus: ((m as any).predationBonus || 0) + 300,
                  } as any;
                }
                return m;
              })
            );

            addLog('苗床粘液獣が相手モンスターを捕食した！', 'info');
          },
        } as any);

        return true;
      },
    },
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '破壊時: 1度だけ効果無効で復活',
      effect: (context: TriggerContext) => {
        const { card, addLog, slotIndex } = context;
        if (!card || slotIndex === undefined) return false;

        // 復活済みフラグをチェック
        if ((card as any).hasRevived) {
          addLog('苗床粘液獣: すでに1度復活済みのため復活できない', 'info');
          return false;
        }

        const { myField, setMyField, currentPlayer } = getPlayerContext(context);

        // 復活処理
        const revivedCard = {
          ...card,
          hasRevived: true,
          currentHp: (card as any).hp ?? 0,
          effectDisabled: true,
          owner: currentPlayer, // 常時効果のターゲット判定用（再設定）
        } as any;

        const newField = [...myField];
        newField[slotIndex] = revivedCard;
        setMyField(newField);

        addLog('苗床粘液獣が効果無効で復活した！', 'info');
        return true;
      },
    },
  ],

  /**
   * C0000006: 新・超覚醒粘液獣ハイパー
   * 【召喚時】場にいる粘液獣1体につき、自身の攻撃力を1000アップ。
   * 【基本技】相手モンスター1体を飲み込む（2ターン後に消化、破壊時は吐き出す）
   */
  C0000006: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 場の粘液獣1体につき攻撃力+1000',
      effect: (context: TriggerContext) => {
        const { slotIndex, addLog } = context;
        const { myField } = getPlayerContext(context);

        // 場の粘液獣の数をカウント（自身を除く）
        const slimeCount = myField.filter(
          (m, idx) =>
            m &&
            idx !== slotIndex &&
            hasCategory(m, '【スライム】')
        ).length;

        if (slimeCount > 0) {
          const boost = slimeCount * 1000;
          modifyAttack(context as any, boost, slotIndex, false, true);
          addLog(
            `新・超覚醒粘液獣ハイパーの召喚時効果: 攻撃力+${boost}！`,
            'info'
          );
        } else {
          addLog(
            '新・超覚醒粘液獣ハイパー: 場に他の粘液獣がいないため強化なし',
            'info'
          );
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンド時: 飲み込んだモンスターを消化中',
      effect: (context: TriggerContext) => {
        const { slotIndex, card, addLog } = context;
        const { setMyField, setOpponentGraveyard } = getPlayerContext(context);

        // 飲み込んだモンスターがいない場合はスキップ
        const cardAny = card as any;
        if (!cardAny || !cardAny.swallowedMonster) {
          return;
        }

        const newDigestCounter = (cardAny.digestCounter || 1) - 1;

        if (newDigestCounter <= 0) {
          // 消化完了：相手の墓地に送る
          const digestedMonster = { ...cardAny.swallowedMonster };
          delete digestedMonster.originalSlotIndex;

          setOpponentGraveyard((prev) => [...prev, digestedMonster]);

          // 飲み込み情報をクリア
          setMyField((prev) =>
            prev.map((m, idx) => {
              if (idx === slotIndex && m) {
                const updated = { ...m } as any;
                delete updated.swallowedMonster;
                delete updated.digestCounter;
                return updated;
              }
              return m;
            })
          );

          addLog(
            `新・超覚醒粘液獣ハイパーが${digestedMonster.name}を完全に消化した！`,
            'damage'
          );
        } else {
          // 消化中：カウンターを減らす
          setMyField((prev) =>
            prev.map((m, idx) => {
              if (idx === slotIndex && m) {
                return { ...m, digestCounter: newDigestCounter } as any;
              }
              return m;
            })
          );

          addLog(
            `新・超覚醒粘液獣ハイパーが${cardAny.swallowedMonster.name}を消化中...（あと${newDigestCounter}ターン）`,
            'info'
          );
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '破壊時: 飲み込んだモンスターを吐き出す',
      effect: (context: TriggerContext) => {
        const { card, addLog, summoningSickness = true } = context;
        const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

        // 飲み込んだモンスターがいない場合はスキップ
        const cardAny = card as any;
        if (!cardAny || !cardAny.swallowedMonster) {
          return;
        }

        const swallowed = cardAny.swallowedMonster;

        // 相手フィールドの空きスロットを探す
        const emptySlotIndex = opponentField.findIndex((slot) => slot === null);

        if (emptySlotIndex !== -1) {
          // 空きがあればフィールドに戻す
          const restoredMonster = {
            ...swallowed,
            canAttack: !summoningSickness, // 召喚酔い設定に依存
            currentHp: swallowed.currentHp || swallowed.hp,
            currentAttack: swallowed.currentAttack || swallowed.attack,
          };
          delete restoredMonster.originalSlotIndex;

          setOpponentField((prev) => {
            const newField = [...prev];
            newField[emptySlotIndex] = restoredMonster;
            return newField;
          });

          addLog(
            `新・超覚醒粘液獣ハイパーが破壊され、${swallowed.name}が吐き出された！`,
            'info'
          );
        } else {
          // 空きがなければ墓地に送る
          const deadMonster = { ...swallowed };
          delete deadMonster.originalSlotIndex;

          setOpponentGraveyard((prev) => [...prev, deadMonster]);

          addLog(
            `新・超覚醒粘液獣ハイパーが破壊されたが、場が満杯のため${swallowed.name}は墓地へ`,
            'info'
          );
        }
      },
    },
  ],

  /**
   * C0000008: 粘液獣・胞子
   * 【召喚時】デッキから《粘液獣》と名の付くカード1枚を手札に加える。
   */
  C0000008: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: デッキから粘液獣カードをサーチ',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingDeckReview } = context;
        const { myDeck, setMyDeck, setMyHand } = getPlayerContext(context);

        // デッキから《粘液獣》と名の付くカードを検索
        const slimeCards = myDeck.filter(card =>
          card.name && card.name.includes('粘液獣')
        );

        if (slimeCards.length === 0) {
          addLog('粘液獣・胞子: デッキに《粘液獣》カードがありません', 'info');
          return false;
        }

        // サーチ処理（選択後に実行）
        const addToHand = (selectedCard: CardWithId) => {
          setMyDeck(prev => prev.filter(c => c.uniqueId !== selectedCard.uniqueId));
          setMyHand(prev => [...prev, selectedCard]);
          addLog(`粘液獣・胞子: ${selectedCard.name}を手札に加えた`, 'info');
        };

        // 1枚のみの場合は自動選択
        if (slimeCards.length === 1) {
          addToHand(slimeCards[0]);
          return true;
        }

        // 複数ある場合はデッキ確認UIを使用
        if (setPendingDeckReview) {
          setPendingDeckReview({
            cards: slimeCards,
            message: '手札に加える《粘液獣》カードを選択',
            count: 1,
            callback: (selectedCards: CardWithId[]) => {
              if (selectedCards && selectedCards.length > 0) {
                addToHand(selectedCards[0]);
              }
            },
            selectionOwner: context.currentPlayer,
          } as any);
          return true;
        }

        // UIが使えない場合は最初のカードを自動選択
        addToHand(slimeCards[0]);
        return true;
      },
    },
  ],

  /**
   * C0000010: 粘液獣・融合体
   * 【召喚時】このカードと場の他の《粘液獣》1体を融合し、このカードの攻撃力を融合素材の合計に変更する。
   */
  C0000010: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: '召喚時: 場の粘液獣1体と融合して攻撃力を合計値に',
      effect: (context: TriggerContext) => {
        const { slotIndex, card, addLog } = context;
        const { myField, setMyField } = getPlayerContext(context);

        // 他の粘液獣を探す
        const targetIndex = myField.findIndex(
          (m, idx) =>
            m &&
            idx !== slotIndex &&
            hasCategory(m, '【スライム】')
        );

        if (targetIndex === -1) {
          addLog('粘液獣・融合体: 融合する対象がいない', 'info');
          return false;
        }

        // TODO: UI実装時に対象選択が必要
        if (!card || slotIndex === undefined) return false;
        const target = myField[targetIndex];
        if (!target) return false;
        const cardAny = card as any;
        const targetAny = target as any;
        const totalAttack =
          (cardAny.currentAttack ?? cardAny.attack ?? 0) +
          (targetAny.currentAttack ?? targetAny.attack ?? 0);

        // 対象を破壊
        const newField = [...myField];
        newField[targetIndex] = null;

        // 融合体の攻撃力を更新
        const fusedCard = {
          ...card,
          attack: totalAttack,
          currentAttack: totalAttack,
        } as any;
        newField[slotIndex] = fusedCard;

        setMyField(newField);
        addLog(
          `粘液獣・融合体が${target.name}と融合！攻撃力が${totalAttack}になった！`,
          'info'
        );
        return true;
      },
    },
  ],

  /**
   * C0000011: 粘液獣・暴走体
   * 【召喚時】場にいる《粘液獣》1体を破壊し、そのコスト×300ダメージを相手プレイヤーに与える。
   */
  C0000011: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 場の《粘液獣》1体を破壊してコスト×300ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField } = getPlayerContext(context);

        // 《粘液獣》と名の付くモンスターを探す（名前判定）
        const targetIndex = myField.findIndex(
          (m) => m && m.name && m.name.includes('粘液獣')
        );

        if (targetIndex === -1) {
          addLog('粘液獣・暴走体: 破壊する《粘液獣》がいない', 'info');
          return false;
        }

        // TODO: UI実装時に対象選択が必要
        const target = myField[targetIndex];
        if (!target) return false;
        const damage = (target.cost || 0) * 300;

        destroyMonster(context as any, targetIndex, false);
        conditionalDamage(context as any, damage, 'opponent');

        addLog(
          `粘液獣・暴走体が${target.name}を破壊し、${damage}ダメージ！`,
          'damage'
        );
        return true;
      },
    },
  ],

  /**
   * C0000127: ゴシック・ローズ
   * 【召喚時】デッキから《ゴシック》または《鎖》魔法カード1枚を手札に加える。
   * 【自壊時】相手に400ダメージ。
   */
  C0000127: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: デッキから《ゴシック》/《鎖》魔法カードをサーチ',
      effect: (context: TriggerContext) => {
        const foundCard = searchCard(context as any, (card) => {
          return Boolean(
            card.type === 'magic' &&
            card.name &&
            (card.name.includes('ゴシック') || card.name.includes('鎖'))
          );
        });

        if (foundCard) {
          const { addLog } = context;
          addLog(`ゴシック・ローズ: ${foundCard.name}を手札に加えた`, 'info');
          return true;
        }
        return false;
      },
    },
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '破壊時: 相手に400ダメージ',
      effect: (context: TriggerContext) => {
        conditionalDamage(context as any, 400, 'opponent');
        return true;
      },
    },
  ],

  /**
   * C0000128: 鎖縛のメイド
   * 【召喚時】相手モンスター1体の攻撃力をターン終了時まで0にする。
   */
  C0000128: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手モンスター1体の攻撃力を0に',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentField, setOpponentField } = getPlayerContext(context);

        // 対象を探す
        const targetIndex = opponentField.findIndex((m) => m !== null);
        if (targetIndex === -1) {
          addLog('鎖縛のメイド: 対象がいない', 'info');
          return false;
        }

        // TODO: UI実装時に対象選択が必要
        const target = opponentField[targetIndex];
        if (!target) return false;
        const newField = [...opponentField];
        newField[targetIndex] = {
          ...target,
          currentAttack: 0,
          attackSealedUntilEndPhase: true,
        } as any;
        setOpponentField(newField);

        addLog(
          `鎖縛のメイドが${target.name}の攻撃力を0にした！`,
          'info'
        );
        return true;
      },
    },
  ],

  /**
   * C0000130: 檻の中の歌姫
   * 【召喚時】相手の場にいるモンスター1体を檻に閉じ込める（攻撃不可、効果無効）。相手は自分のターン開始時にSP1で解除可能。
   */
  C0000130: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手モンスター1体を檻に閉じ込める',
      effect: (context: TriggerContext) => {
        // 状態異常システムを使用してCAGED状態を付与
        return selectAndApplyStatusToOpponent(
          context as any,
          STATUS_EFFECT_TYPES.CAGED,
          {
            duration: -1, // 永続（SP解除まで）
          },
          '檻の中の歌姫'
        );
      },
    },
  ],

  /**
   * C0000131: 鎖の守護者
   * 【召喚時】このカードに「守護（1度だけダメージ半減）」を付与する。
   */
  C0000131: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 守護（1度だけダメージ半減）を付与',
      effect: (context: TriggerContext) => {
        const { slotIndex, card, addLog } = context;
        if (slotIndex === undefined || !card) return false;
        const { myField, setMyField } = getPlayerContext(context);

        const newField = [...myField];
        newField[slotIndex] = {
          ...card,
          hasGuardian: true, // 守護フラグ
        } as any;
        setMyField(newField);

        addLog('鎖の守護者に守護が付与された！', 'info');
        return true;
      },
    },
  ],

  /**
   * C0000311: 粘液獣・暴君
   * 【召喚時】場にいるモンスター1体を破壊する。
   * 【自分エンドフェイズ時】相手プレイヤーに400ダメージを与える。
   */
  C0000311: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 場のモンスター1体を破壊',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingMonsterTarget, slotIndex } = context;
        const { myField, opponentField } = getPlayerContext(context);

        // 自分・相手両方の場から破壊可能なモンスターを確認（自分自身は除外）
        const myValidTargets = myField
          .map((m, idx) => ({ monster: m, index: idx }))
          .filter(({ monster, index }) => monster !== null && index !== slotIndex);
        const opponentValidTargets = opponentField
          .map((m, idx) => ({ monster: m, index: idx }))
          .filter(({ monster }) => monster !== null);

        if (myValidTargets.length === 0 && opponentValidTargets.length === 0) {
          addLog('粘液獣・暴君: 破壊する対象がいない', 'info');
          return false;
        }

        // モンスター選択UIを表示
        if (setPendingMonsterTarget) {
          setPendingMonsterTarget({
            message: '破壊するモンスターを選択してください',
            targetPlayer: 'both',
            validTargets: myValidTargets.map((t) => t.index),
            validOpponentTargets: opponentValidTargets.map((t) => t.index),
            callback: (selectedIndex: number, isOpponent: boolean) => {
              destroyMonster(context as any, selectedIndex, isOpponent);
              const targetField = isOpponent ? opponentField : myField;
              const targetName = targetField[selectedIndex]?.name || 'モンスター';
              addLog(`粘液獣・暴君が${targetName}を破壊した！`, 'info');
            },
            selectionOwner: context.currentPlayer,
          } as any);
        } else {
          // フォールバック：相手優先で自動選択
          if (opponentValidTargets.length > 0) {
            destroyMonster(context as any, opponentValidTargets[0].index, true);
            addLog('粘液獣・暴君が相手モンスターを破壊した！', 'info');
          } else {
            destroyMonster(context as any, myValidTargets[0].index, false);
            addLog('粘液獣・暴君が自分のモンスターを破壊した！', 'info');
          }
        }
        return true;
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンド時: 相手に400ダメージ',
      effect: (context: TriggerContext) => {
        conditionalDamage(context as any, 400, 'opponent');
        return true;
      },
    },
  ],

  /**
   * C0000353: ヴォランティス・エテルノス
   * 【犠現】: 自分の場からモンスター1体を破壊し、そのコスト分このカードのSPコストを軽減（最大3軽減）。
   */
  C0000353: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: '召喚時（犠現）: 場のモンスター破壊でコスト軽減済み',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        // 犠現効果は召喚コスト支払い時に処理済み
        // このトリガーは記録用
        addLog('ヴォランティス・エテルノスが犠現効果で召喚された', 'info');
        return true;
      },
    },
  ],

  /**
   * C0000404: 呪森の狼王グリムヴァルド
   * 【自分エンドフェイズ時】相手プレイヤーに300ダメージを与える。
   */
  C0000404: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンド時: 相手に300ダメージ',
      effect: (context: TriggerContext) => {
        conditionalDamage(context as any, 300, 'opponent');
        return true;
      },
    },
  ],

  /**
   * C0000405: 灰塵の骨翼アッシュドラゴン
   * 【自攻撃時】相手モンスターに与えるダメージを300アップ。
   */
  C0000405: [
    {
      type: TRIGGER_TYPES.ON_ATTACK,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '攻撃時: ダメージ+300',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        addLog('灰塵の骨翼アッシュドラゴン: 攻撃ダメージ+300！', 'info');
        // accumulatedDamageBonus を使用（React state更新の非同期問題を回避）
        context.accumulatedDamageBonus = (context.accumulatedDamageBonus || 0) + 300;
      },
    },
  ],

  /**
   * C0000413: 群狼長ヴァルグレイス
   * 【自攻撃時】相手モンスターの攻撃力がこのカードより低い場合、そのダメージを400アップ。
   */
  C0000413: [
    {
      type: TRIGGER_TYPES.ON_ATTACK,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '攻撃時: 相手が弱い場合ダメージ+400',
      effect: (context: TriggerContext) => {
        const { attacker, target, attackerIndex, addLog } = context;
        const { myField, setMyField } = getPlayerContext(context);

        // ダイレクトアタック時は対象がないのでスキップ
        if (!target || !attacker || attackerIndex === undefined) {
          return false;
        }

        // 攻撃力比較: 自分の攻撃力 > 相手の攻撃力
        const attackerAny = attacker as any;
        const targetAny = target as any;
        const myAtk = attackerAny.currentAttack ?? attackerAny.attack ?? 0;
        const targetAtk = targetAny.currentAttack ?? targetAny.attack ?? 0;

        if (myAtk > targetAtk) {
          // ダメージボーナスフラグを設定
          const newField = [...myField];
          newField[attackerIndex] = {
            ...attacker,
            attackDamageBonus: (attackerAny.attackDamageBonus || 0) + 400,
          } as any;
          setMyField(newField);
          addLog(`群狼長ヴァルグレイス: 相手が弱いため攻撃ダメージ+400！`, 'info');
        }
        return true;
      },
    },
  ],

  /**
   * C0000415: 呪影の牙狼ウルフェイン
   * 【自攻撃時】相手の反撃ダメージを受けない。
   */
  C0000415: [
    {
      type: TRIGGER_TYPES.ON_ATTACK,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '攻撃時: 反撃ダメージ無効',
      effect: (context: TriggerContext) => {
        const { slotIndex, card, addLog } = context;
        if (slotIndex === undefined || !card) return false;
        const { myField, setMyField } = getPlayerContext(context);

        const newField = [...myField];
        newField[slotIndex] = {
          ...card,
          noCounterDamage: true, // 反撃無効フラグ
        } as any;
        setMyField(newField);

        addLog('呪影の牙狼ウルフェイン: 反撃ダメージを受けない！', 'info');
        return true;
      },
    },
  ],

  /**
   * C0000416: 霧角の鹿ケルニグリス
   * 【自分エンドフェイズ時】自分のライフを300回復。
   */
  C0000416: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンド時: ライフ300回復',
      effect: (context: TriggerContext) => {
        healLife(context, 300, true);
        return true;
      },
    },
  ],

  /**
   * C0000417: 棘の剛毛猪イノブリスト
   * 【被攻撃時】このカードが与える反撃は50％で計算する。
   */
  C0000417: [
    {
      type: TRIGGER_TYPES.ON_ATTACKED,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '被攻撃時: 反撃ダメージ50%',
      effect: (context: TriggerContext) => {
        const { slotIndex, card, addLog } = context;
        if (slotIndex === undefined || !card) return false;
        const { myField, setMyField } = getPlayerContext(context);

        const newField = [...myField];
        newField[slotIndex] = {
          ...card,
          counterDamageRate: 0.5, // 反撃50%
        } as any;
        setMyField(newField);

        addLog('棘の剛毛猪イノブリスト: 反撃ダメージ50%！', 'info');
        return true;
      },
    },
  ],

  /**
   * C0000003: 粘液獣の群生地 (フィールドカード)
   * 【自分エンドフェイズ時】場にいる粘液獣1体につき相手プレイヤーに300ダメージを与える。
   */
  C0000003: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンド時: 場の粘液獣1体につき相手に300ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField } = getPlayerContext(context);

        // 粘液獣の数をカウント
        const slimeCount = myField.filter(
          (m) => m && hasCategory(m, '【スライム】')
        ).length;

        if (slimeCount > 0) {
          const damage = slimeCount * 300;
          conditionalDamage(context as any, damage, 'opponent');
          addLog(
            `粘液獣の群生地: 粘液獣${slimeCount}体で${damage}ダメージ！`,
            'damage'
          );
        }
        return true;
      },
    },
  ],

  /**
   * C0000019: 粘液の巣窟 (フィールドカード)
   * 【自分エンドフェイズ時】墓地の《粘液獣》1体を場に戻す。
   * 【自分エンドフェイズ時】場に《粘液獣》が3体以上いる場合、自分のライフを500回復。
   */
  C0000019: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      priority: TRIGGER_PRIORITIES.HIGH,
      description: 'エンド時: 墓地の《粘液獣》1体を復活',
      effect: (context: TriggerContext) => {
        return reviveFromGraveyard(
          context as any,
          (card) => {
            // 《粘液獣》と名の付くモンスターを対象（名前判定）
            return Boolean(
              card.type === 'monster' &&
              card.name &&
              card.name.includes('粘液獣')
            );
          },
          false // 弱体化なし
        );
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      priority: TRIGGER_PRIORITIES.NORMAL,
      description: 'エンド時: 《粘液獣》3体以上でライフ500回復',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField } = getPlayerContext(context);

        // 《粘液獣》と名の付くモンスターの数をカウント（名前判定）
        const slimeCount = myField.filter(
          (m) => m && m.name && m.name.includes('粘液獣')
        ).length;

        if (slimeCount >= 3) {
          healLife(context, 500, true);
          addLog('粘液の巣窟: 《粘液獣》3体以上でライフ500回復！', 'heal');
          return true;
        } else {
          addLog('粘液の巣窟: 《粘液獣》が3体未満のため回復なし', 'info');
          return false;
        }
      },
    },
  ],

  /**
   * C0000139: ゴシック・フォートレス (フィールドカード)
   * 【自分エンドフェイズ時】相手モンスター全体に300ダメージ。
   */
  C0000139: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンド時: 相手モンスター全体に300ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentField, setOpponentField } = getPlayerContext(context);

        let damageDealt = false;
        const newField = opponentField.map((monster) => {
          if (monster) {
            damageDealt = true;
            const monsterAny = monster as any;
            const newHp = (monsterAny.currentHp ?? monsterAny.hp ?? 0) - 300;
            return {
              ...monster,
              currentHp: Math.max(0, newHp),
            };
          }
          return monster;
        });

        if (damageDealt) {
          setOpponentField(newField);
          addLog(
            'ゴシック・フォートレス: 相手モンスター全体に300ダメージ！',
            'damage'
          );
        }

        return damageDealt;
      },
    },
  ],

  /**
   * C0000141: 禁忌の檻 (フィールドカード)
   * 【自分エンドフェイズ時】場にいる《ゴシック》モンスター1体につき、相手モンスター1体に300ダメージ。
   */
  C0000141: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンド時: ゴシックモンスター1体につき相手に300ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField, opponentField, setOpponentField } = getPlayerContext(context);

        // ゴシックモンスターの数をカウント
        const gothicCount = myField.filter(
          (m) => m && m.name && m.name.includes('ゴシック')
        ).length;

        if (gothicCount === 0) {
          addLog('禁忌の檻: ゴシックモンスターがいないためダメージなし', 'info');
          return false;
        }

        // 相手モンスターにダメージ
        let damageCount = 0;
        const newField = opponentField.map((monster) => {
          if (monster && damageCount < gothicCount) {
            damageCount++;
            const monsterAny = monster as any;
            const newHp = (monsterAny.currentHp ?? monsterAny.hp ?? 0) - 300;
            return {
              ...monster,
              currentHp: Math.max(0, newHp),
            };
          }
          return monster;
        });

        if (damageCount > 0) {
          setOpponentField(newField);
          addLog(
            `禁忌の檻: ゴシック${gothicCount}体で相手に${damageCount * 300}ダメージ！`,
            'damage'
          );
        }

        return damageCount > 0;
      },
    },
  ],

  /**
   * C0000366: 天翔秘島 (フィールドカード)
   * 【自分エンドフェイズ時】自分の属性ごとの《ヴォランティス》モンスター1体につき、以下の効果を適用
   */
  C0000366: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンド時: 属性ごとのヴォランティスで効果発動',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField } = getPlayerContext(context);

        // 属性別のヴォランティスモンスターをカウント
        const attributeCounts: Record<string, number> = {};
        myField.forEach((monster) => {
          if (monster && monster.name && monster.name.includes('ヴォランティス')) {
            const attr = monster.attribute || 'なし';
            attributeCounts[attr] = (attributeCounts[attr] || 0) + 1;
          }
        });

        const attributeCount = Object.keys(attributeCounts).length;

        if (attributeCount === 0) {
          addLog('天翔秘島: ヴォランティスモンスターがいない', 'info');
          return false;
        }

        // 属性ごとに効果を適用（簡略版）
        // TODO: 完全な実装には各属性の効果を定義する必要がある
        let totalEffect = 0;
        Object.entries(attributeCounts).forEach(([attr, count]) => {
          totalEffect += (count as number);
        });

        addLog(
          `天翔秘島: ${attributeCount}属性のヴォランティスで効果発動！`,
          'info'
        );

        // 簡易的に回復効果を適用
        healLife(context, totalEffect * 100, true);

        return true;
      },
    },
  ],

  /**
   * C0000018: 粘液の沼地 (フィールドカード)
   * 【常時】場にいる《粘液獣》の攻撃力を300アップ。(※トリガー対象外)
   * 【相手モンスター攻撃時】相手プレイヤーに200ダメージを与える。
   */
  C0000018: [
    {
      type: TRIGGER_TYPES.ON_OPPONENT_ATTACK,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '相手攻撃時: 相手プレイヤーに200ダメージ',
      effect: (context: TriggerContext) => {
        const { currentPlayer, addLog } = context;
        // 相手が攻撃した時、そのプレイヤーにダメージを与える
        conditionalDamage(context as any, 200, 'opponent');
        addLog('粘液の沼地: 相手の攻撃に反応して200ダメージ！', 'damage');
        return true;
      },
    },
  ],

  /**
   * C0000245: 鎖の守護霊リアリス
   * 【相手攻撃時】場に『鎖縛の幻姫リアノン』がいる場合、相手の攻撃を1ターンに1度無効化し、相手モンスターに500ダメージを与える。
   */
  C0000245: [
    {
      type: TRIGGER_TYPES.ON_OPPONENT_ATTACK,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '相手攻撃時: リアノンがいれば攻撃無効化＋500ダメージ',
      effect: (context: TriggerContext) => {
        const { card, addLog } = context;
        if (!card) return false;
        const cardAny = card as any;
        const { myField } = getPlayerContext(context);

        // トリガー使用済みチェック（1ターンに1度）
        if (cardAny.triggerUsedThisTurn) {
          addLog('鎖の守護霊リアリス: 今ターンは既に発動済み', 'info');
          return false;
        }

        // 場に鎖縛の幻姫リアノン（C0000244）がいるかチェック
        const hasRiannon = myField.some(
          (m) => m && (m.id === 'C0000244' || m.name?.includes('鎖縛の幻姫リアノン'))
        );

        if (!hasRiannon) {
          addLog('鎖の守護霊リアリス: 場に鎖縛の幻姫リアノンがいない', 'info');
          return false;
        }

        // 攻撃を無効化（TODO: 実際の攻撃無効化処理が必要）
        // 相手の攻撃モンスターに500ダメージ
        // TODO: 攻撃モンスターの特定が必要。現時点では相手プレイヤーにダメージ
        conditionalDamage(context as any, 500, 'opponent');

        addLog(
          '鎖の守護霊リアリス: 攻撃を無効化し、相手に500ダメージ！',
          'info'
        );

        // 使用済みフラグを立てる
        cardAny.triggerUsedThisTurn = true;

        return true;
      },
    },
  ],

  /**
   * C0000138: 禁忌のゴシッククラウン
   * 【常時】自分のSPトークンはすべてレスト状態になる。(※トリガー対象外)
   * 【自壊時】自分のライフに2000ダメージ。
   */
  C0000138: [
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '破壊時: 自分に2000ダメージ',
      effect: (context: TriggerContext) => {
        conditionalDamage(context as any, 2000, 'self');
        const { addLog } = context;
        addLog('禁忌のゴシッククラウンが破壊され、自分に2000ダメージ！', 'damage');
        return true;
      },
    },
  ],

  /**
   * C0000246: 鎖縛の禁忌姫リアノン・エターナル
   * 【召喚時】相手モンスター1体を1ターン行動不能にする。
   * 【常時】相手の魔法カードを1ターンに1度無効化する。(※トリガー対象外)
   * 【自壊時】2000ダメージを受ける。
   */
  C0000246: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      priority: TRIGGER_PRIORITIES.HIGH,
      description: '召喚時: 相手モンスター1体を1ターン行動不能に',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentField, setOpponentField } = getPlayerContext(context);

        // 対象を探す
        const targetIndex = opponentField.findIndex((m) => m !== null);
        if (targetIndex === -1) {
          addLog('鎖縛の禁忌姫リアノン・エターナル: 対象がいない', 'info');
          return false;
        }

        // TODO: UI実装時に対象選択が必要
        const target = opponentField[targetIndex];
        if (!target) return false;
        const newField = [...opponentField];
        newField[targetIndex] = {
          ...target,
          canAttack: false,
          effectDisabled: true,
          immobilizedUntilNextTurn: true,
        } as any;
        setOpponentField(newField);

        addLog(
          `鎖縛の禁忌姫リアノン・エターナルが${target.name}を行動不能にした！`,
          'info'
        );
        return true;
      },
    },
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      priority: TRIGGER_PRIORITIES.NORMAL,
      description: '破壊時: 自分に2000ダメージ',
      effect: (context: TriggerContext) => {
        conditionalDamage(context as any, 2000, 'self');
        const { addLog } = context;
        addLog(
          '鎖縛の禁忌姫リアノン・エターナルが破壊され、自分に2000ダメージ！',
          'damage'
        );
        return true;
      },
    },
  ],

  // ========================================
  // C0000129: ゴシック・プリンセス
  // 【相手モンスター攻撃時】その攻撃力を300ダウン、自分のライフを200回復
  // ========================================
  C0000129: [
    {
      type: TRIGGER_TYPES.ON_ATTACKED,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      priority: TRIGGER_PRIORITIES.NORMAL,
      description: '相手モンスター攻撃時: 攻撃力-300、自分LP+200',
      effect: (context: TriggerContext) => {
        const { addLog, attackerIndex, currentPlayer } = context;
        const { opponentField, setOpponentField, setMyLife, myLife } =
          getPlayerContext(context);

        // 攻撃してきた相手モンスターの攻撃力を300ダウン
        if (attackerIndex !== undefined && opponentField[attackerIndex]) {
          const newField = [...opponentField];
          const attacker = newField[attackerIndex];
          if (attacker) {
            const attackerAny = attacker as any;
            const oldAtk = attackerAny.currentAttack ?? attackerAny.attack ?? 0;
            const newAtk = Math.max(0, oldAtk - 300);
            newField[attackerIndex] = {
              ...attacker,
              currentAttack: newAtk,
            } as any;
            setOpponentField(newField);

            addLog(
              `ゴシック・プリンセスの効果で${attacker.name}の攻撃力が300ダウン！(${oldAtk} → ${newAtk})`,
              'info'
            );
          }
        }

        // 自分のライフを200回復
        setMyLife((prev) => prev + 200);
        addLog(`ゴシック・プリンセスの効果で自分のライフを200回復！`, 'heal');

        return true;
      },
    },
  ],

  // ========================================
  // C0000453: ジャイアント・ビー
  // 【自壊時】相手モンスター1体に500ダメージを与える
  // ========================================
  C0000453: [
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '自壊時: 相手モンスター1体に500ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingMonsterTarget } = context;
        const { opponentField, setOpponentField, setOpponentGraveyard } =
          getPlayerContext(context);

        // 相手モンスターを確認
        const validTargets = opponentField
          .map((m, idx) => ({ monster: m, index: idx }))
          .filter(({ monster }) => monster !== null);

        if (validTargets.length === 0) {
          addLog('ジャイアント・ビー: 相手フィールドにモンスターがいません', 'info');
          return;
        }

        const applyDamage = (targetIndex: number) => {
          const target = opponentField[targetIndex];
          if (!target) return;

          const newHp = target.currentHp - 500;
          if (newHp <= 0) {
            setOpponentField(prev => {
              const newField = [...prev];
              newField[targetIndex] = null;
              return newField;
            });
            setOpponentGraveyard(prev => [...prev, target]);
            addLog(`ジャイアント・ビーの効果: ${target.name}に500ダメージで破壊！`, 'damage');
          } else {
            setOpponentField(prev => {
              const newField = [...prev];
              newField[targetIndex] = { ...target, currentHp: newHp };
              return newField;
            });
            addLog(`ジャイアント・ビーの効果: ${target.name}に500ダメージ！`, 'damage');
          }
        };

        // 1体のみの場合は自動選択
        if (validTargets.length === 1) {
          applyDamage(validTargets[0].index);
          return;
        }

        // 複数いる場合は選択UI
        if (setPendingMonsterTarget) {
          setPendingMonsterTarget({
            message: '500ダメージを与える相手モンスターを選択',
            targetPlayer: 'opponent',
            callback: (selectedIndex: number) => {
              applyDamage(selectedIndex);
            },
          } as any);
        } else {
          applyDamage(validTargets[0].index);
        }
      },
    },
  ],

  // ========================================
  // C0000454: 大きく硬い甲殻を持つ獣
  // 【召喚時】相手のフィールドカード・フェイズカードを1枚破壊する
  // 【常時】このカードは反撃ダメージを受けない（monsterCards.jsで実装済み）
  // ========================================
  C0000454: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手フィールド/フェイズカードを1枚破壊',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { setOpponentFieldCard, setOpponentPhaseCard, opponentFieldCard, opponentPhaseCard } =
          getPlayerContext(context);

        // 相手のフィールドカードがあれば破壊
        if (opponentFieldCard) {
          addLog(`大きく硬い甲殻を持つ獣の効果: 相手のフィールドカード「${opponentFieldCard.name}」を破壊！`, 'damage');
          setOpponentFieldCard(null);
          return;
        }

        // 相手のフェイズカードがあれば破壊
        if (opponentPhaseCard) {
          addLog(`大きく硬い甲殻を持つ獣の効果: 相手のフェイズカード「${opponentPhaseCard.name}」を破壊！`, 'damage');
          setOpponentPhaseCard(null);
          return;
        }

        addLog('大きく硬い甲殻を持つ獣: 相手にフィールドカード/フェイズカードがありません', 'info');
      },
    },
  ],

  // =============================================================================
  // 深海領域カウンターカード
  // =============================================================================

  /**
   * C0000534: 干潟の呪術師
   * 【召喚時】相手の深海領域のカードを1枚選び、深度0で強制浮上させる。
   * 相手が浮上コストを払えない場合、そのカードは破壊される。
   */
  C0000534: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手の深海領域カード1枚を強制浮上',
      effect: (context: TriggerContext) => {
        const { addLog, currentPlayer, setPendingDeepSeaSelection } = context;

        // 相手の深海領域を取得
        const opponentPlayer = currentPlayer === 1 ? 2 : 1;
        const opponentDeepSeaZone = opponentPlayer === 1
          ? (context as any).p1DeepSeaZone
          : (context as any).p2DeepSeaZone;
        const setOpponentDeepSeaZone = opponentPlayer === 1
          ? (context as any).setP1DeepSeaZone
          : (context as any).setP2DeepSeaZone;

        // 深海領域がない、または開放されていない、またはカードがない場合
        if (!opponentDeepSeaZone || !opponentDeepSeaZone.isUnlocked || opponentDeepSeaZone.cards.length === 0) {
          addLog('干潟の呪術師の効果: 相手の深海領域にカードがありません', 'info');
          return;
        }

        // 強制浮上処理
        const performForceSurface = (deepSeaIndex: number) => {
          const targetCard = opponentDeepSeaZone.cards[deepSeaIndex];
          const result = forceSurfaceCard(opponentDeepSeaZone, deepSeaIndex, true);

          if (!result.success) {
            addLog(`干潟の呪術師の効果: 浮上に失敗`, 'info');
            return;
          }

          addLog(`干潟の呪術師の効果: ${targetCard.card.name}を深度0で強制浮上！`, 'damage');
          setOpponentDeepSeaZone(result.updatedZone);

          // 浮上コストをチェック（深度0なので元コストが必要）
          const surfaceCost = result.spCost;
          const opponentSP = opponentPlayer === 1
            ? (context as any).p1ActiveSP
            : (context as any).p2ActiveSP;
          const setOpponentActiveSP = opponentPlayer === 1
            ? (context as any).setP1ActiveSP
            : (context as any).setP2ActiveSP;
          const opponentField = opponentPlayer === 1
            ? (context as any).p1Field
            : (context as any).p2Field;
          const setOpponentField = opponentPlayer === 1
            ? (context as any).setP1Field
            : (context as any).setP2Field;
          const setOpponentGraveyard = opponentPlayer === 1
            ? (context as any).setP1Graveyard
            : (context as any).setP2Graveyard;

          // フィールドに空きがあるかチェック
          const emptySlot = opponentField ? opponentField.findIndex((s: MonsterInstance | null) => s === null) : -1;

          if (opponentSP >= surfaceCost && emptySlot !== -1) {
            // コストを払えて、フィールドに空きがある場合 → 浮上成功
            if (setOpponentActiveSP) {
              setOpponentActiveSP((prev: number) => prev - surfaceCost);
            }
            // フィールドに配置
            const surfacedMonster: MonsterInstance = {
              ...targetCard.card,
              uniqueId: `${targetCard.card.id}_forced_${Date.now()}`,
              currentAttack: targetCard.card.attack ?? 0,
              currentHp: targetCard.card.hp ?? 0,
              canAttack: false, // 強制浮上なので攻撃不可
              charges: [],
              statusEffects: [],
              owner: opponentPlayer,
            };
            if (setOpponentField) {
              setOpponentField((prev: (MonsterInstance | null)[]) => {
                const newField = [...prev];
                const slot = newField.findIndex(s => s === null);
                if (slot !== -1) {
                  newField[slot] = surfacedMonster;
                }
                return newField;
              });
            }
            addLog(`${targetCard.card.name}がフィールドに浮上（コスト${surfaceCost}）`, 'info');
          } else {
            // コストを払えない、またはフィールドに空きがない場合 → 破壊
            const reason = opponentSP < surfaceCost
              ? `コスト${surfaceCost}を払えず`
              : 'フィールドに空きがなく';
            addLog(`${targetCard.card.name}は${reason}破壊された！`, 'damage');
            if (setOpponentGraveyard) {
              setOpponentGraveyard((prev: CardWithId[]) => [...prev, targetCard.card]);
            }
          }
        };

        // 1枚のみの場合は自動選択
        if (opponentDeepSeaZone.cards.length === 1) {
          performForceSurface(0);
          return;
        }

        // 複数ある場合は選択UI
        if (setPendingDeepSeaSelection) {
          setPendingDeepSeaSelection({
            message: '強制浮上させる深海領域のカードを選択',
            cards: opponentDeepSeaZone.cards,
            count: 1,
            callback: (selectedIndices: number[]) => {
              if (selectedIndices && selectedIndices.length > 0) {
                performForceSurface(selectedIndices[0]);
              }
            },
            selectionOwner: currentPlayer,
          } as any);
        } else {
          // フォールバック: 最初のカードを選択
          performForceSurface(0);
        }
      },
    },
  ],

  /**
   * C0000538: 待ち伏せる大蛇
   * 【常時】相手モンスターが深海領域から浮上した時、そのモンスターに800ダメージ。
   * 【常時】このカードが水属性モンスターを撃破した時、カードを1枚ドローする。
   */
  C0000538: [
    {
      type: TRIGGER_TYPES.ON_SURFACE,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      priority: TRIGGER_PRIORITIES.HIGH,
      description: '相手モンスター浮上時: 800ダメージ',
      // このトリガーは相手の浮上時に発動
      targetSelf: false,
      effect: (context: TriggerContext) => {
        const { addLog, currentPlayer } = context;
        const surfacedCard = (context as any).surfacedCard;

        // 相手の浮上時のみ発動
        const triggerOwner = (context as any).owner || currentPlayer;
        if (triggerOwner === currentPlayer) {
          // 自分の浮上時は発動しない
          return;
        }

        if (!surfacedCard) return;

        // 浮上したモンスターに800ダメージ
        const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);
        const monsterIndex = opponentField.findIndex(
          (m: MonsterInstance | null) => m && m.uniqueId === surfacedCard.uniqueId
        );

        if (monsterIndex === -1) return;

        const target = opponentField[monsterIndex];
        if (!target) return;

        const newHp = (target.currentHp || target.hp || 0) - 800;
        addLog(`待ち伏せる大蛇の効果: ${target.name}に800ダメージ！`, 'damage');

        if (newHp <= 0) {
          addLog(`${target.name}が破壊された！`, 'damage');
          setOpponentGraveyard((prev: CardWithId[]) => [...prev, target]);
          setOpponentField((prev: (MonsterInstance | null)[]) => {
            const newField = [...prev];
            newField[monsterIndex] = null;
            return newField;
          });
        } else {
          setOpponentField((prev: (MonsterInstance | null)[]) => {
            const newField = [...prev];
            newField[monsterIndex] = { ...target, currentHp: newHp };
            return newField;
          });
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_ATTACK_SUCCESS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '水属性モンスター撃破時: カード1枚ドロー',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        // useCombat.ts から destroyedCard として渡される
        const destroyedCard = (context as any).destroyedCard;

        // 水属性モンスターを撃破した場合のみ発動
        if (destroyedCard && destroyedCard.attribute === '水') {
          addLog('待ち伏せる大蛇の効果: 水属性モンスターを撃破！1枚ドロー！', 'info');
          drawCards(context as any, 1);
        }
      },
    },
  ],

  /**
   * C0000539: アビスイータードラゴン
   * 【召喚時】相手の深海領域のカードを2枚まで破壊する。
   * 基本技: 相手モンスター1体に自身の攻撃力半分のダメージを与える。
   */
  C0000539: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手の深海領域のカードを2枚まで破壊',
      effect: (context: TriggerContext) => {
        const { addLog, currentPlayer } = context;

        // 相手の深海領域を取得
        const opponentPlayer = currentPlayer === 1 ? 2 : 1;
        const opponentDeepSeaZone = opponentPlayer === 1
          ? (context as any).p1DeepSeaZone
          : (context as any).p2DeepSeaZone;
        const setOpponentDeepSeaZone = opponentPlayer === 1
          ? (context as any).setP1DeepSeaZone
          : (context as any).setP2DeepSeaZone;
        const setOpponentGraveyard = opponentPlayer === 1
          ? (context as any).setP1Graveyard
          : (context as any).setP2Graveyard;

        if (!opponentDeepSeaZone || opponentDeepSeaZone.cards.length === 0) {
          addLog('アビスイータードラゴンの効果: 相手の深海領域にカードがありません', 'info');
          return;
        }

        // 最大2枚破壊
        const cardsToDestroy = Math.min(2, opponentDeepSeaZone.cards.length);
        const destroyedCards: CardWithId[] = [];

        let updatedZone = opponentDeepSeaZone;
        for (let i = 0; i < cardsToDestroy; i++) {
          const result = destroyDeepSeaCard(updatedZone, 0);
          if (result.destroyedCard) {
            destroyedCards.push(result.destroyedCard);
            updatedZone = result.updatedZone;
          }
        }

        if (destroyedCards.length > 0) {
          const cardNames = destroyedCards.map((c) => c.name).join('、');
          addLog(`アビスイータードラゴンの効果: ${cardNames}を破壊！`, 'damage');
          setOpponentDeepSeaZone(updatedZone);
          if (setOpponentGraveyard) {
            setOpponentGraveyard((prev: CardWithId[]) => [...prev, ...destroyedCards]);
          }
        }
      },
    },
  ],
};

/**
 * 原始属性カードがトリガー実装を持っているかチェック
 * @param {string} cardId - カードID
 * @returns {boolean} トリガー実装を持っている場合true
 */
export const hasPrimitiveCardTrigger = (cardId: string): boolean => {
  return Boolean(cardId && (primitiveCardTriggers as Record<string, unknown>)[cardId] !== undefined);
};

/**
 * 原始属性カードのトリガーを取得
 * @param {string} cardId - カードID
 * @returns {Array|null} トリガー配列、または null
 */
export const getPrimitiveCardTriggers = (cardId: string): TriggerDefinition[] | null => {
  const triggers = primitiveCardTriggers as unknown as Record<string, TriggerDefinition[]>;
  if (!cardId || !triggers[cardId]) {
    return null;
  }
  return triggers[cardId];
};

/**
 * 実装済み原始属性カード数を取得
 * @returns {number} 実装済みカード数
 */
export const getPrimitiveCardCount = () => {
  return Object.keys(primitiveCardTriggers).length;
};
